import React, { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  MESSAGE_TYPES,
  handleInputChangeWithScroll,
} from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  clearFieldError,
  executeValidationWithArrays,
  handleArrayItemRemoveWithValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "New Visitor", path: `/newvisitor` },
];

function NewVisitorEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "newVisitorContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    page_title: "",
    page_image: {},
    page_content: "",
    concern_image: {},
    concern1: "",
    concern2: "",
    concern3: "",
    merit1_image: {},
    merit1_title: "",
    merit1_content: "",
    merit2_image: {},
    merit2_title: "",
    merit2_content: "",
    merit3_image: {},
    merit3_title: "",
    merit3_content: "",
    merit4_image: {},
    merit4_title: "",
    merit4_content: "",
    conversion_text: "",
    conversion_limit: "",
    about_text: "",
    reviews: [],
  });

  // ドラッグ&ドロップ並べ替え処理
  const handleReviewReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          reviews: reorderedItems.map((review, index) => ({
            ...review,
            display_order: index,
          })),
        };

        clearValidationErrors();

        // iframeに全データを送信
        const iframe = document.querySelector("iframe");

        if (iframe?.contentWindow) {
          const message = {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
            isFullUpdate: true,
          };

          iframe.contentWindow.postMessage(
            message,
            process.env.REACT_APP_ORIGIN
          );

          // 確認のため、少し遅延してもう一度送信
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              message,
              process.env.REACT_APP_ORIGIN
            );
          }, 100);
        } else {
          console.warn("iframe not found or contentWindow not available:", {
            iframe: !!iframe,
            contentWindow: !!iframe?.contentWindow,
          });
        }

        return newData;
      });
    },
    [contentKey]
  );

  // ドラッグ&ドロップフック
  const { getDraggableProps } = useDragAndDrop(
    pageContent.reviews || [],
    handleReviewReorder
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_page_newvisitor_content/`
      );
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      console.log("newvisitor data", data);

      // reviewsの構造を正規化（imageが文字列の場合は空オブジェクトに変換）
      if (data.reviews) {
        data.reviews = data.reviews.map((review) => ({
          ...review,
          image: typeof review.image === "string" ? {} : review.image || {},
        }));
      }

      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // バリデーションルールを定義
  const validationRules = {
    page_title: { required: true },
    page_content: { required: true },
    concern1: { required: true },
    concern2: { required: true },
    concern3: { required: true },
    concern_image: { required: true },
    merit1_title: { required: true },
    merit1_content: { required: true },
    merit1_image: { required: true },
    merit2_title: { required: true },
    merit2_content: { required: true },
    merit2_image: { required: true },
    merit3_title: { required: true },
    merit3_content: { required: true },
    merit3_image: { required: true },
    merit4_title: { required: true },
    merit4_content: { required: true },
    merit4_image: { required: true },
    conversion_text: { required: true },
    about_text: { required: true },
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    const arrayValidationRules = {
      reviews: {
        comment: { required: true },
        reviewer: { required: true },
        image: { required: true },
      },
    };

    const fieldLabels = {
      page_title: "ページタイトル",
      page_content: "ページ本文",
      concern1: "不安要素1",
      concern2: "不安要素2",
      concern3: "不安要素3",
      concern_image: "不安要素背景画像",
      merit1_title: "メリット1タイトル",
      merit1_content: "メリット1内容",
      merit1_image: "メリット1画像",
      merit2_title: "メリット2タイトル",
      merit2_content: "メリット2内容",
      merit2_image: "メリット2画像",
      merit3_title: "メリット3タイトル",
      merit3_content: "メリット3内容",
      merit3_image: "メリット3画像",
      merit4_title: "メリット4タイトル",
      merit4_content: "メリット4内容",
      merit4_image: "メリット4画像",
      conversion_text: "コンバージョンテキスト",
      about_text: "スタジオについて",
    };

    if (
      !executeValidationWithArrays(
        pageContent,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    const formData = { page_content: pageContent };

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_newvisitor_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "更新に失敗しました");

      setMessage(data.message || "正常に更新されました");
      await fetchPageContentData();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      alert(`更新に失敗しました: ${error.message}`);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = () => {
    clearValidationErrors();
    setMessage("");
    fetchPageContentData();
  };

  // 入力変更処理（単一フィールド用）
  const handleInputChange = (e) => {
    // エラークラスをクリア
    clearFieldError(e.target);

    handleInputChangeWithScroll(e, {
      contentKey,
      setData: setPageContent,
    });
  };

  // メディア選択処理
  const handleMediaSelect = (fieldName, media) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        [fieldName]: media,
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_${fieldName}`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // レビュー追加
  const addReview = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newItem = {
        comment: "",
        reviewer: "",
        image: {},
      };

      let newReviews;
      if (insertAfterIndex !== null) {
        // 指定位置に挿入
        newReviews = [
          ...prev.reviews.slice(0, insertAfterIndex + 1),
          newItem,
          ...prev.reviews.slice(insertAfterIndex + 1),
        ];
      } else {
        // 末尾に追加
        newReviews = [...prev.reviews, newItem];
      }

      const newData = {
        ...prev,
        reviews: newReviews,
      };

      // iframeに全データを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // レビュー削除
  const removeReview = (index) => {
    handleArrayItemRemoveWithValidation(
      index,
      setPageContent,
      "reviews",
      {
        comment: { required: true },
        reviewer: { required: true },
        image: { required: true },
      },
      {},
      (newData) => {
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  // レビュー変更
  const handleReviewChange = (index, field, value, eventType = "change") => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        reviews: prev.reviews.map((review, i) =>
          i === index ? { ...review, [field]: value } : review
        ),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_review_${index}_${field}`;
        const messageType =
          eventType === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.TEXT_UPDATE;

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: targetClass,
            contentKey,
            fieldName: field,
            index,
            value,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // レビュー画像選択
  const handleReviewImageSelect = (index, media) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        reviews: prev.reviews.map((review, i) =>
          i === index ? { ...review, image: media } : review
        ),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_review_${index}_image`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName: "image",
            index,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      setLoadingText("アップロード中");

      // ファイルを取得（input change event or drag & drop event）
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];

      if (!file) {
        console.warn("No file found in event");
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        console.log("Upload response:", data);
        await fetchMediaFiles();

        if (data.images?.[0]) {
          console.log("Selecting uploaded media:", data.images[0]);
          selectMedia(data.images[0]);
        } else {
          console.warn("No images in upload response");
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* ページ上部 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <div
                        className="text-inputs"
                        style={{
                          display: "grid",
                          gap: "0.75em",
                          height: "100%",
                        }}
                      >
                        <textarea
                          className="input1"
                          name="page_title"
                          value={pageContent.page_title}
                          onChange={handleInputChange}
                          onFocus={handleInputChange}
                          placeholder="ページタイトル"
                          data-label="ページタイトル"
                          data-required="true"
                          style={{ minHeight: "initial", height: "4em" }}
                        />
                        <textarea
                          className="input2"
                          name="page_content"
                          value={pageContent.page_content}
                          onChange={handleInputChange}
                          onFocus={handleInputChange}
                          placeholder="ページ本文"
                          data-label="ページ本文"
                          data-required="true"
                          style={{ minHeight: "initial", height: "6em" }}
                        />
                      </div>
                      <div className="input3 img-form">
                        <MediaSelector
                          label="ページ画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="page_image"
                          selectedMedia={pageContent.page_image}
                          onSelect={(media) =>
                            handleMediaSelect("page_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 不安要素 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <div
                        className="text-inputs"
                        style={{
                          display: "grid",
                          gap: "0.75em",
                          height: "100%",
                        }}
                      >
                        <input
                          type="text"
                          name="concern1"
                          value={pageContent.concern1}
                          onChange={handleInputChange}
                          onFocus={handleInputChange}
                          placeholder="不安要素1"
                          data-label="不安要素1"
                          data-required="true"
                        />
                        <input
                          type="text"
                          name="concern2"
                          value={pageContent.concern2}
                          onChange={handleInputChange}
                          onFocus={handleInputChange}
                          placeholder="不安要素2"
                          data-label="不安要素2"
                          data-required="true"
                        />
                        <input
                          type="text"
                          name="concern3"
                          value={pageContent.concern3}
                          onChange={handleInputChange}
                          onFocus={handleInputChange}
                          placeholder="不安要素3"
                          data-label="不安要素3"
                          data-required="true"
                        />
                      </div>
                      <div className="input3 img-form">
                        <MediaSelector
                          label="不安要素背景画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="concern_image"
                          selectedMedia={pageContent.concern_image}
                          onSelect={(media) =>
                            handleMediaSelect("concern_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* メリット1-4 */}
              <section>
                {[1, 2, 3, 4].map((num) => (
                  <div className="area3">
                    <div className="item">
                      <div className="forms">
                        <div
                          className="text-inputs"
                          style={{
                            display: "grid",
                            gap: "0.75em",
                            height: "100%",
                          }}
                        >
                          <textarea
                            className="input1"
                            name={`merit${num}_title`}
                            value={pageContent[`merit${num}_title`]}
                            onChange={handleInputChange}
                            onFocus={handleInputChange}
                            placeholder={`メリット${num}タイトル`}
                            data-label={`メリット${num}タイトル`}
                            data-required="true"
                            style={{ minHeight: "initial", height: "4em" }}
                          />
                          <textarea
                            className="input2"
                            name={`merit${num}_content`}
                            value={pageContent[`merit${num}_content`]}
                            onChange={handleInputChange}
                            onFocus={handleInputChange}
                            placeholder={`メリット${num}内容`}
                            data-label={`メリット${num}内容`}
                            data-required="true"
                            style={{ minHeight: "initial", height: "6em" }}
                          />
                        </div>
                        <div className="input3 img-form">
                          <MediaSelector
                            label={`メリット${num}画像`}
                            aspectRatio="9:16"
                            type="image"
                            fieldName={`merit${num}_image`}
                            selectedMedia={pageContent[`merit${num}_image`]}
                            onSelect={(media) =>
                              handleMediaSelect(`merit${num}_image`, media)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </section>

              {/* コンバージョン */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div
                      className="forms"
                      style={{ gridTemplateColumns: "1fr" }}
                    >
                      <textarea
                        className="input1"
                        name="conversion_text"
                        value={pageContent.conversion_text}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        placeholder="コンバージョンテキスト"
                        data-label="コンバージョンテキスト"
                        data-required="true"
                      />
                      <input
                        type="text"
                        className="input2"
                        name="conversion_limit"
                        value={pageContent.conversion_limit}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        placeholder="キャンペーン期限"
                        data-label="キャンペーン期限"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* スタジオについて */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div
                      className="forms"
                      style={{ gridTemplateColumns: "1fr" }}
                    >
                      <textarea
                        className="input2"
                        name="about_text"
                        value={pageContent.about_text}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        placeholder="スタジオについて"
                        data-label="スタジオについて"
                        data-required="true"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* レビュー */}
              <section>
                <div className="area2">
                  {pageContent.reviews?.map((review, index) => (
                    <div
                      className="item"
                      key={index}
                      {...getDraggableProps(index)}
                    >
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            onClick={() => removeReview(index)}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => addReview(index)}
                          ></i>
                        </div>
                      </div>
                      <div className="forms">
                        <input
                          type="text"
                          className="input1"
                          name={`review${index}_reviewer`}
                          value={review.reviewer}
                          onChange={(e) =>
                            handleReviewChange(
                              index,
                              "reviewer",
                              e.target.value,
                              "change"
                            )
                          }
                          onFocus={(e) =>
                            handleReviewChange(
                              index,
                              "reviewer",
                              e.target.value,
                              "focus"
                            )
                          }
                          placeholder="レビュアー名"
                          data-label="レビュアー名"
                          data-required="true"
                        />

                        <textarea
                          className="input2"
                          name={`review${index}_comment`}
                          value={review.comment}
                          onChange={(e) =>
                            handleReviewChange(
                              index,
                              "comment",
                              e.target.value,
                              "change"
                            )
                          }
                          onFocus={(e) =>
                            handleReviewChange(
                              index,
                              "comment",
                              e.target.value,
                              "focus"
                            )
                          }
                          placeholder="レビューコメント"
                          style={{ minHeight: "initial", height: "6em" }}
                          data-label="レビューコメント"
                          data-required="true"
                        />

                        <div className="input3 img-form">
                          <MediaSelector
                            label="レビュー画像"
                            aspectRatio="9:16"
                            type="image"
                            fieldName={`review${index}_image`}
                            selectedMedia={review.image}
                            onSelect={(media) =>
                              handleReviewImageSelect(index, media)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!pageContent.reviews ||
                    pageContent.reviews.length === 0) && (
                    <div className="item">
                      <button type="button" onClick={addReview}>
                        レビューを追加
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/newvisitor" data={pageContent} contentKey={contentKey} />
    </>
  );
}

export default function NewVisitorEditPage() {
  return (
    <MediaProvider>
      <NewVisitorEditPageContent />
    </MediaProvider>
  );
}
